import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { exerciseLibrary } from '../../services/api';
import { errorList } from './shared';

/*
 * Importar ativos (imagens e vídeos) em lote. Duas fases com as mesmas regras do servidor:
 *   1. Pré-visualizar — diz, ficheiro a ficheiro, a que exercício ficaria associado e o que acontece (acrescentar, ignorar porque
 *      já existe, ou erro com o motivo). Não grava nada.
 *   2. Importar — grava o que passou. Repetir o mesmo lote não duplica nada (a identidade é o conteúdo do ficheiro).
 *
 * O exercício deduz-se do nome do ficheiro (leg-press.webp, leg-press-2.webp, leg-press.mp4) ou do manifesto (JSON opcional com
 * origem, licença, atribuição e endereço de origem). Uma linha com erro pode corrigir-se aqui mesmo, indicando o exercício.
 */

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm';

const ACTION = {
  add: { label: 'Acrescentar', tone: 'accent' },
  skip: { label: 'Ignorar', tone: '' },
  error: { label: 'Erro', tone: 'bad' },
};

export default function MediaImportDialog({ onClose, onImported }) {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [manifest, setManifest] = useState([]);      // [{ file, exercise, origin, license, attribution, sourceUrl, alt }]
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const counts = useMemo(() => ({
    add: report?.rows.filter((r) => r.action === 'add').length ?? 0,
    skip: report?.rows.filter((r) => r.action === 'skip').length ?? 0,
    error: report?.rows.filter((r) => r.action === 'error').length ?? 0,
  }), [report]);

  const run = async (apply, nextManifest = manifest, nextFiles = files) => {
    setBusy(true);
    setError(null);
    try {
      const call = apply ? exerciseLibrary.mediaImport : exerciseLibrary.mediaImportPreview;
      const { data } = await call(nextFiles, nextManifest);
      setReport(data);
      if (apply) onImported?.(data);
    } catch (e) {
      setError(errorList(e, 'Não foi possível processar o lote.').join(' '));
    } finally {
      setBusy(false);
    }
  };

  const pickFiles = (event) => {
    const list = Array.from(event.target.files || []);
    setFiles(list);
    setReport(null);
    if (list.length > 0) run(false, manifest, list);
  };

  const pickManifest = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      if (!Array.isArray(parsed)) throw new Error('lista');
      setManifest(parsed);
      setError(null);
      if (files.length > 0) run(false, parsed, files);
    } catch {
      setError('O manifesto não é um JSON válido: uma lista de { file, exercise, origin, license, attribution, sourceUrl, alt }.');
    }
  };

  const fixAssociation = (fileName, exercise) => {
    const value = exercise.trim();
    if (!value) return;
    const next = [...manifest.filter((m) => m.file !== fileName), { ...(manifest.find((m) => m.file === fileName) || {}), file: fileName, exercise: value }];
    setManifest(next);
    run(false, next, files);
  };

  const done = report?.applied;

  return createPortal(
    <div className="lib-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lib-dialog" role="dialog" aria-modal="true" aria-label="Importar ativos em lote">
        <div className="lib-dialog-head">
          <h2>Importar imagens e vídeos em lote</h2>
          <button type="button" className="lib-btn small" onClick={onClose}>Fechar</button>
        </div>

        <div className="lib-dialog-body">
          <p className="lib-hint">
            Escolha vários ficheiros. O exercício deduz-se do nome (<code>leg-press.webp</code>, <code>leg-press-2.webp</code>, <code>leg-press.mp4</code>);
            uma miniatura chama-se <code>leg-press.thumb.webp</code>. Vídeos entram <strong>pendentes</strong> até serem aprovados.
          </p>

          <div className="lib-field">
            <label className="lib-label" htmlFor="batch-files">Ficheiros (até 100)</label>
            <input id="batch-files" ref={fileRef} className="lib-input" type="file" multiple accept={ACCEPT} onChange={pickFiles} disabled={busy} />
          </div>
          <div className="lib-field">
            <label className="lib-label" htmlFor="batch-manifest">Manifesto (opcional, JSON)</label>
            <input id="batch-manifest" className="lib-input" type="file" accept="application/json,.json" onChange={pickManifest} disabled={busy} />
            <span className="lib-hint">Indica o exercício, a origem, a licença, a atribuição e o endereço de origem de cada ficheiro. Conteúdo aberto ou licenciado exige os três últimos.</span>
          </div>

          {error && <div className="lib-notice bad" role="alert">{error}</div>}
          {busy && <div className="lib-hint" role="status">A processar…</div>}

          {report && (
            <div data-testid="batch-report">
              <div className="lib-banner" role="status">
                <strong>{done ? 'Importação concluída' : 'Pré-visualização'}</strong>
                <span>{report.total} ficheiros</span>
                <span data-testid="batch-added">{report.added} {done ? 'acrescentados' : 'a acrescentar'}</span>
                <span data-testid="batch-skipped">{report.skipped} ignorados (já existem)</span>
                <span data-testid="batch-errors">{report.errors} com erro</span>
              </div>

              {/* Uma lista (não uma tabela): cada ficheiro é um bloco que cabe em qualquer largura, sem partir palavras. */}
              <ul className="lib-batch-list" aria-label="Resultado por ficheiro">
                {report.rows.map((r) => (
                  <li key={r.file} data-testid={`batch-row-${r.file}`}>
                    <div className="lib-batch-head">
                      <strong>{r.file}</strong>
                      <span className={`lib-chip ${ACTION[r.action]?.tone || ''}`}>{ACTION[r.action]?.label || r.action}</span>
                    </div>
                    <div className="lib-hint">
                      Exercício: {r.exerciseName || '—'}{r.kind === 'video' ? ' · vídeo' : ''}{r.hasThumb ? ' · com miniatura' : ''}
                    </div>
                    {r.reason && <div className="lib-hint">{r.reason}</div>}
                    {r.action === 'error' && !done && (
                      <input className="lib-input" placeholder="Exercício (nome, slug ou id) — Enter" aria-label={`Exercício para ${r.file}`}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fixAssociation(r.file, e.currentTarget.value); } }} />
                    )}
                  </li>
                ))}
              </ul>
              {done && counts.error > 0 && (
                <div className="lib-notice warn" role="status">
                  {counts.error} ficheiros ficaram por importar. Corrija o nome ou o manifesto e envie só esses: os que já entraram são ignorados.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="lib-dialog-foot">
          <button type="button" className="lib-btn" onClick={onClose}>Fechar</button>
          <button type="button" className="lib-btn primary" disabled={busy || done || !report || counts.add === 0} onClick={() => run(true)}>
            {`Importar ${counts.add} ficheiro${counts.add === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
